import React, { useState } from "react";
import {
  Toolbar,
  Typography,
  Button,
  Box,
  Container as MuiContainer,
  Link as MuiLink,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  Divider
} from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/authContext"; // Import useAuth
import { StyledAppBar, Logo } from "./styles";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {useIsMobile} from "hooks/useIsMobile";
/**
 * Container component that renders the application's header, navigation, and main content area.
 * It provides navigation functionality, a responsive drawer for smaller screens,
 * and logout functionality for authenticated users.
 * @param {object} props - The props object for the Container component.
 * @param {React.ReactNode} props.children - The child components to be rendered inside the main content area.
 * @returns {React.ReactElement} The rendered Container component.
 */
function Container({ children }) {
  const { currentUser, logout } = useAuth(); // Use the useAuth hook
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  const handleLogout = () => {
    logout();
    navigate("/"); // Redirect to home page after logout
  };

  const handleBackClick = () => {
    navigate(-1);  // This will navigate back
  };
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <StyledAppBar position="static">
        <Toolbar>
          {location.pathname !== "/" && (
            <IconButton edge="start" color="inherit" aria-label="back" onClick={handleBackClick}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h6" style={{ flexGrow: 1 }}>
            <MuiLink component={Link} to="/" color="inherit" underline="none">
              <Logo src="/assets/hdfs-logo.png" alt="Harley Davidson Financial Services" />
            </MuiLink>
          </Typography>
          {isSmallScreen ? (
            <IconButton edge="end" color="inherit" aria-label="menu" onClick={handleDrawerToggle}>
              <MenuIcon />
            </IconButton>
          ) : (
            <Box display="flex">
              <Button color="inherit" component={Link} to="/">Home</Button>
              <Button color="inherit" component={Link} to="/dealer/login">Dealers</Button>
              <Button
                color="inherit"
                component={MuiLink}
                href="https://www.harley-davidson.com/us/en/tools/flex-financing.html" // Replace with your desired external URL
                target="_blank"
                rel="noopener noreferrer"
              >About</Button>
              {currentUser && (
                <Button color="inherit" onClick={handleLogout}>Logout</Button> // Logout button
              )}
            </Box>
          )}
        </Toolbar>
      </StyledAppBar>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        PaperProps={{
          sx: {
            paddingTop: "4vh",
            width: "50vw",
            backgroundColor: "rgba(255, 255, 255, 1)",
            boxShadow: "0 0 10px rgba(0, 0, 0, 0.3)"
          }
        }}
      >
        <List sx={{backgroundColor: "white"}}>
          <ListItem button component={Link} to="/" onClick={handleDrawerToggle}>Home</ListItem>
          <Divider />
          <ListItem button component={Link} to="/dealer/login" onClick={handleDrawerToggle}>Dealers</ListItem>
          <Divider />
          <ListItem button component={Link} to="/about" onClick={handleDrawerToggle}>About</ListItem>
          <Divider />
          <ListItem button component={Link} to="/help" onClick={handleDrawerToggle}>Help</ListItem>
        </List>
      </Drawer>
      <MuiContainer sx={isMobile ? { padding: 0} : null}>
        <Box my={isMobile? 0 : 4}>
          {children}
        </Box>
      </MuiContainer>
      <footer style={{ backgroundColor: "#f5f5f5", margin: "70px 0 0 0", padding: "20px 0", marginTop: "auto", position: "relative" }}>
        <MuiContainer maxWidth="lg">
          <Typography variant="body2" color="textSecondary" align="center">
                © {new Date().getFullYear()} H-D U.S.A., LLC.
          </Typography>
          <Box mt={2} display="flex" justifyContent="center">
            <MuiLink component={Link} to="/privacy-policy" color="inherit" style={{ marginRight: "15px" }}>
                  Privacy Policy
            </MuiLink>
            <MuiLink component={Link} to="/terms" color="inherit">
                  Terms & Conditions
            </MuiLink>
          </Box>
        </MuiContainer>
      </footer>
    </div>
  );
}

export default Container;
